/** After join, wait this long for LiveKit to finish the first publish. */
export const JOIN_GRACE_MS = 4000
/** Camera is ON but no new frame for this long → treat as a dead device. */
export const FRAME_STALL_MS = 2000
/** Camera sender has emitted no new frames for this long. */
export const SEND_STALL_MS = 4000
/** Stop auto-restarting a camera that stays dead after this many tries. */
export const FROZEN_MAX_AUTO = 3

/** Intended ON (LiveKitRoom audio/video true) but nothing published. */
export function joinPublishMissing(opts: {
  micIntended: boolean
  camIntended: boolean
  hasMicPub: boolean
  hasCamPub: boolean
}): boolean {
  return (opts.micIntended && !opts.hasMicPub) || (opts.camIntended && !opts.hasCamPub)
}

/** Padlock Allow is not itself proof of capture — re-acquire only if we still lack the intended pubs. */
export function shouldAcquireAfterPermissionGrant(opts: {
  reason: string
  micIntended: boolean
  camIntended: boolean
  hasMicPub: boolean
  hasCamPub: boolean
}): boolean {
  return opts.reason === 'permission_granted' && joinPublishMissing(opts)
}

/** No new decoded frame since watch start or last frame, for dwellMs. */
export function cameraLooksFrozen(opts: {
  watchingSince: number
  lastFrameAt: number | null
  now: number
  dwellMs?: number
}): boolean {
  const last = opts.lastFrameAt ?? opts.watchingSince
  return opts.now - last >= (opts.dwellMs ?? FRAME_STALL_MS)
}

export function freezeWatchPaused(hidden: boolean): boolean {
  return hidden
}

export function canAutoFrozenRestart(consecutive: number, max = FROZEN_MAX_AUTO): boolean {
  return consecutive < max
}

export function senderFramesAdvanced(before: number, after: number): boolean {
  return after > before
}

export function senderFramesLookLive(before: number, after: number, minDelta = 8): boolean {
  return Number.isFinite(before) && Number.isFinite(after) && after - before >= minDelta
}

export type RecoverLabel = 'success' | 'degraded' | 'local_only' | 'unverified' | 'fail'

export function recoverLabel(opts: {
  hasPeer: boolean
  sendLooksLive: boolean | null
  deviceFail: boolean
}): RecoverLabel {
  if (opts.deviceFail) return 'fail'
  if (opts.sendLooksLive === null) return 'unverified'
  if (!opts.sendLooksLive) return 'degraded'
  return opts.hasPeer ? 'success' : 'local_only'
}

export function sendLooksStalled(opts: {
  lastSentAt: number | null
  watchingSince: number
  now: number
  dwellMs?: number
}): boolean {
  const last = opts.lastSentAt ?? opts.watchingSince
  return opts.now - last >= (opts.dwellMs ?? SEND_STALL_MS)
}

export function shouldActOnStaleStats(cancelled: boolean): boolean {
  return !cancelled
}

export type CameraRecoveryReason = 'frozen' | 'send_stalled' | 'track_ended'
export type CameraRecoveryStep = 'restart' | 'republish' | 'fault'

export function isVerifiedCameraRecovery(reason: string): reason is CameraRecoveryReason {
  return reason === 'frozen' || reason === 'send_stalled' || reason === 'track_ended'
}

export function nextCameraRecovery(opts: {
  reason: CameraRecoveryReason
  verifiedRestartFailed: boolean
  consecutive: number
}): CameraRecoveryStep {
  if (opts.consecutive >= FROZEN_MAX_AUTO) return 'fault'
  return opts.reason === 'send_stalled' || opts.verifiedRestartFailed ? 'republish' : 'restart'
}

export function recoverProof(ranSenderCheck: boolean): 'sender_frames_8_2s' | null {
  return ranSenderCheck ? 'sender_frames_8_2s' : null
}
